import { RANKS, rankOf } from "../data/levels.js";
import { GROUPME, LIBRARY } from "../data/library.js";
import { CALENDAR } from "../data/calendar.js";
import { OPS } from "../data/ops.js";

// What each tier can see, derived from the SAME data and thresholds the app
// gates on — not a hand-maintained copy. If a minLevel changes in the data, or
// a new GroupMe room is added, this preview follows automatically.
//
// Where a gate is hard-coded in a screen rather than data-driven, it is listed
// here explicitly with a `where` pointing at the file, so the two can be
// checked against each other.
const PAGE_GATES = [
  { name: "Home", min: "member", where: "src/screens/Home.jsx" },
  { name: "Search the hub", min: "member", where: "src/components/Menu.jsx" },
  { name: "My Dashboard", min: "member", where: "src/components/Menu.jsx" },
  { name: "Onboarding", min: "member", where: "src/screens/Onboard.jsx" },
  { name: "Culture Codes", min: "member", where: "src/screens/Culture.jsx" },
  { name: "myTRiBE+ Journey", min: "member", where: "src/screens/Tribe.jsx" },
  { name: "myTRiBE Calendar", min: "member", where: "src/screens/Calendar.jsx" },
  { name: "Prayer & Testimony Wall", min: "member", where: "src/screens/Wall.jsx" },
  { name: "Join the Dream Team", min: "member", where: "src/screens/DreamTeam.jsx" },
  { name: "CLLXTV Music", min: "member", where: "src/screens/Worship.jsx" },
  { name: "SOPs & Playbooks", min: "member", where: "src/screens/Ops.jsx" },
  { name: "Report an issue", min: "member", where: "src/screens/Report.jsx" },
  { name: "LEADERSHiP+", min: "leader", where: "src/components/Menu.jsx" },
  { name: "Request a meeting", min: "leader", where: "src/screens/Meet.jsx" },
  { name: "Org Chart", min: "director", where: "src/components/Menu.jsx" },
  { name: "Role Admin", min: "senior", where: "netlify/functions/admin.mts" },
];

// Capabilities enforced server-side in netlify/functions/storage.mts. These are
// the ones that actually matter — the client gates are convenience only.
const ACTION_GATES = [
  { name: "Post on the public wall", min: "member", where: "storage.mts" },
  { name: "Send a private note to leadership", min: "member", where: "storage.mts" },
  { name: "Post in class chatter", min: "member", where: "storage.mts" },
  { name: "Read the leadership inbox", min: "director", where: "READ_RULES" },
  { name: "Reply to inbox submissions", min: "director", where: "APPEND_RULES" },
  { name: "Post an urgent broadcast", min: "director", where: "APPEND_RULES" },
  { name: "Delete any stored key", min: "director", where: "storage.mts" },
  { name: "Every team's playbook", min: "leader", where: "src/screens/Ops.jsx" },
  { name: "Director-level operations", min: "director", where: "src/screens/Ops.jsx" },
  { name: "Your serving schedule link", min: "leader", where: "src/screens/Calendar.jsx" },
  { name: "Change anyone's role", min: "senior", where: "netlify/functions/admin.mts" },
];

const visible = (userRank, min) => userRank >= rankOf(min);

// Groups of things a tier can or cannot reach, ready to render.
export function capabilitiesFor(level) {
  const r = rankOf(level);
  const mark = (items) =>
    items.map((i) => ({ ...i, allowed: visible(r, i.min) }));

  return [
    {
      group: "Pages",
      items: mark(PAGE_GATES.map((p) => ({ name: p.name, min: p.min, where: p.where }))),
    },
    {
      group: "Actions",
      items: mark(ACTION_GATES.map((a) => ({ name: a.name, min: a.min, where: a.where }))),
    },
    {
      group: "GroupMe rooms",
      items: mark(
        GROUPME.map((g) => ({ name: `${g.icon} ${g.name}`, min: g.minLevel, where: "data/library.js" })),
      ),
    },
    {
      group: "Library folders",
      items: mark(
        LIBRARY.map((f) => ({ name: `${f.icon} ${f.name}`, min: f.minLevel, where: "data/library.js" })),
      ),
    },
    {
      group: "Team playbooks",
      items: mark(
        Object.keys(OPS.teamFolders).map((name) => ({
          name,
          // A member only ever sees their own team's folder; leaders see all.
          min: "leader",
          where: "src/screens/Ops.jsx",
        })),
      ),
    },
    {
      group: "Calendar entries",
      items: mark(
        [...CALENDAR.weekly, ...CALENDAR.monthlyNth]
          .filter((e) => e.minLevel)
          .map((e) => ({
            name: `${e.emoji} ${e.name}`,
            min: e.minLevel,
            where: "data/calendar.js",
          })),
      ),
    },
  ];
}

// Headline counts, for the at-a-glance summary.
export function capabilitySummary(level) {
  const groups = capabilitiesFor(level);
  const all = groups.flatMap((g) => g.items);
  return {
    allowed: all.filter((i) => i.allowed).length,
    total: all.length,
  };
}

// What changes moving between two tiers — the useful bit when reassigning.
export function diffTiers(fromLevel, toLevel) {
  const before = capabilitiesFor(fromLevel).flatMap((g) => g.items);
  const after = capabilitiesFor(toLevel).flatMap((g) => g.items);

  const gained = [];
  const lost = [];
  after.forEach((item, i) => {
    if (item.allowed && !before[i].allowed) gained.push(item.name);
    if (!item.allowed && before[i].allowed) lost.push(item.name);
  });
  return { gained, lost };
}

export { RANKS };
